/**
 * How far along the shorter arm an angle mark reaches.
 *
 * One number for the whole product: the painter draws the arc at this fraction of the arm, and
 * the caption is anchored there so it sits on its own mark. Two copies would drift, and the
 * symptom would be a label floating beside the thing it belongs to.
 */
export const ANGLE_MARK_ARM_FRACTION = 1 / 3;

/**
 * How big an angle mark is when the arms leave it room, in drawing units.
 *
 * Here rather than in the painter, and that move is the fix to a real defect. The arc has
 * *two* limits — a nominal size, and {@link ANGLE_MARK_ARM_FRACTION} of the shorter arm — and
 * only the second one was written down where the domain could see it. So `anchorPosition`
 * placed a caption at a third of the arm however long the arm was, while the painter drew the
 * arc at the nominal size, and on a large figure the letter ended up far outside its own mark:
 * measured on a pyramid, `text alpha "α"` landed 360 units from an arc of radius 22.
 *
 * The note above says two copies of a number would drift. They did — the file simply had one
 * copy of one number and no copy of the other.
 *
 * Drawing units rather than pixels, because that is the only currency the domain has, and it
 * is exact where it matters most: an export draws at scale 1, so a worksheet gets the caption
 * against the arc. On screen at a deep zoom the arc shrinks in world terms and the caption sits
 * a little outside it, which is the small residue of a mark that is notation rather than
 * geometry. The painter still owns the screen-space clamp; this is the same rule, stated where
 * the anchor is computed.
 */
export const ANGLE_MARK_NOMINAL_RADIUS = 22;

/**
 * How far past the outermost arc a *name* sits, in the same units.
 *
 * Small on purpose. A name belongs to its mark, and a gap wide enough to be noticed is a gap
 * wide enough for the reader to wonder which angle it names — on a figure where three marks meet
 * near one corner, that is a real question rather than a fussy one.
 */
export const ANGLE_MARK_LABEL_GAP = 5;

/**
 * Where the *measure* sits, as a fraction of the arc's radius.
 *
 * Inside the arc, which is where a textbook puts it and where it stops competing with everything
 * else near the vertex. It used to be written outside, past the arc and past the ticks, which
 * put the one piece of text that belongs to this angle in the same band of space as the names of
 * the points either side of it.
 *
 * A little past halfway rather than at the middle: the text is centred on this point, so at half
 * the radius it would spill back over the vertex.
 */
export const ANGLE_MARK_VALUE_INSET = 0.58;

/** Between successive arcs of a multi-tick mark, in the same units. */
export const ANGLE_MARK_TICK_GAP = 4.5;

/**
 * The size a mark reaches for, before the arms get a say.
 *
 * Bigger when it carries its own measure, because the measure now sits *inside* it and an arc
 * of the ordinary size has no inside to speak of — `120°` set within a 22-unit radius would
 * reach past the arc at one end and over the vertex at the other. The number is the reason the
 * mark is larger, so the mark is larger exactly when there is a number.
 */
export function angleMarkNominal(showValue: boolean): number {
  return showValue ? ANGLE_MARK_VALUE_RADIUS : ANGLE_MARK_NOMINAL_RADIUS;
}

/** Nominal radius of a mark that writes its own measure inside itself. */
export const ANGLE_MARK_VALUE_RADIUS = 34;

/**
 * How wide a measure is, near enough, in the same units.
 *
 * An estimate rather than a measurement, and deliberately so: the alternative is to thread a
 * font metric from three different output devices into a rule about where a number goes, and the
 * number is at most `123.4°`. Generous, because the cost of over-estimating is a mark drawn
 * slightly larger and the cost of under-estimating is a number written over its own arc.
 */
const VALUE_WIDTH = 32;

/**
 * Whether the measure fits *inside* the arc, which is where it belongs when it can.
 *
 * A wedge is a triangle, so the room across it grows with both the radius and the angle: at a
 * distance `d` along the bisector the opening is `2·d·tan(θ/2)`. A right angle at the ordinary
 * size has plenty; a 40° angle has about half what `56.3°` needs, and writing it inside anyway
 * is what put a number across its own arc and over the vertex.
 *
 * So the answer is conditional, and the condition is arithmetic rather than taste. Outside is
 * the fallback rather than the default — it is what a drafting text does with a narrow angle
 * too, for the same reason.
 */
export function angleMarkValueFitsInside(degrees: number, radius: number): boolean {
  const half = (Math.min(degrees, 180) / 2) * (Math.PI / 180);
  if (half <= 0) return false;
  return 2 * radius * ANGLE_MARK_VALUE_INSET * Math.tan(half) >= VALUE_WIDTH;
}

/**
 * How far from the vertex an angle mark is actually drawn.
 *
 * One function, because this number has now drifted **three** times and each drift produced a
 * different symptom from the same cause. The painter clamps the arc to a nominal size or to a
 * fraction of the shorter arm, whichever is smaller; the two places that needed to agree with it
 * each kept their own arithmetic and each got it wrong:
 *
 * - `anchorPosition` applied only the arm fraction, so on a large figure a caption was anchored
 *   360 units out from an arc of radius 22.
 * - `angleMarkPickPoint` applied only the arm fraction too, and ignored `size` entirely. On the
 *   demo document's right angle the pick point sat 43 units from the vertex while the square was
 *   drawn at 22 — a 21-unit gap against a pick radius of 8, so clicking the mark selected
 *   nothing and none of its controls could be reached at all.
 *
 * The note on {@link ANGLE_MARK_ARM_FRACTION} has said since it was written that two copies of
 * this would drift. The answer is not a third careful copy; it is that there is one.
 */
export function angleMarkRadius(armLength: number, size: number, showValue = false): number {
  return Math.min(angleMarkNominal(showValue), armLength * ANGLE_MARK_ARM_FRACTION) * size;
}

/**
 * Extra room given to a measure that had to go outside, beyond the gap a name gets.
 *
 * A mark can carry both — `anglevalue` writes the number, `text` gives it a name — and both are
 * placed on the bisector. The number, being the one that was pushed out, takes the outer spot.
 *
 * The size is the reason this is a named constant rather than a small nudge. Both runs are
 * *centred* on the bisector, so clearing one another costs half of each: about 14 for `123.4°`
 * and about 6 for a single Greek letter. Nine was tried first and produced `5β.3°` — the name
 * struck through the middle of the number, which is worse than either being slightly far out.
 */
export const ANGLE_MARK_VALUE_CLEARANCE = 24;

/**
 * How far along the bisector the measure is written.
 *
 * The one place the inside-or-outside rule lives. It is needed twice in units that do not
 * match — the painters ask in screen pixels, hit-testing asks in world units — and the answer
 * is the same shape of arithmetic either way, so what is shared is the formula rather than the
 * number. A second copy is how the pick point and the drawn mark came apart before.
 */
export function angleMarkValueDistance(radius: number, degrees: number, ticks: number): number {
  return angleMarkValueFitsInside(degrees, radius)
    ? radius * ANGLE_MARK_VALUE_INSET
    : radius +
        Math.max(0, ticks - 1) * ANGLE_MARK_TICK_GAP +
        ANGLE_MARK_LABEL_GAP +
        ANGLE_MARK_VALUE_CLEARANCE;
}

/**
 * The radius of the *outermost* arc, which is what a name has to clear.
 *
 * A mark of two or three ticks is drawn as concentric arcs stepping outward, so the thing a name
 * sits beside is the last of them and not the first. Placing it against the inner one puts it
 * through the others.
 */
export function angleMarkOuterRadius(
  armLength: number,
  size: number,
  showValue: boolean,
  ticks: number
): number {
  return angleMarkRadius(armLength, size, showValue) + Math.max(0, ticks - 1) * ANGLE_MARK_TICK_GAP;
}

/**
 * How far off a segment its caption sits, in drawing units.
 *
 * A caption anchored to a segment used to land on the segment's *midpoint*, which is wrong twice
 * over. It draws the letters across the line they are labelling — `text AC "2√3a"` printed with
 * the diagonal running through it — and it gives two segments that share a midpoint the same
 * anchor, so a rhombus captioned on both its diagonals set `2√3a` and `2a` on top of each other
 * at O and produced `2a√3a`. Both were visible on the first real problem drawn with two captions.
 *
 * Stepping off perpendicular fixes the first outright and the second by construction: the offset
 * follows the segment's own direction, so two lines crossing at a point send their captions
 * different ways. It cannot separate two captions on segments that are *parallel* and share a
 * midpoint — but those are the same line drawn twice, which the parser now refuses.
 *
 * The same units as the angle mark's gap and for the same reason: an export draws at scale 1.
 */
export const SEGMENT_CAPTION_GAP = 13;

import {
  type Arc,
  type Circle,
  type Ellipse,
  type Line,
  type Polygon,
  type Ray,
  type Segment,
  segmentToLine,
} from '../geometry';
import { type Vec2, vec } from '../math';
import { type RoundBody, type SolidCurve, type SolidFrame, type Vec3 } from '../solid';

/**
 * The value a construction evaluates to.
 *
 * This is runtime state, not document state: it is recomputed from the `EntitySource`
 * DAG and never persisted. Keeping it in a separate type from `Entity` is what lets a
 * saved file describe a construction rather than a snapshot of its appearance.
 *
 * A construction may legitimately have **no value** — two parallel lines have no
 * intersection, three collinear points have no circumcircle. Evaluation returns `null`
 * in those cases rather than throwing or inventing a point. That matters
 * pedagogically: in Sketchpad, dragging a construction through a degenerate
 * configuration makes the dependent objects disappear and then *reappear* on the far
 * side. Treating undefined as an error would break that, and treating it as `NaN` would
 * poison every downstream node.
 */
export type Evaluated =
  | {
      readonly kind: 'point';
      readonly point: Vec2;
      /**
       * Where the point is in space, when it came from a solid.
       *
       * The page has thrown this away, and it is what every metric question about a projected
       * figure needs back: whether a line is covered, whether an angle is right, where the foot
       * of a perpendicular really falls. Absent for an ordinary point, which has no space to be
       * in — it *is* the plane.
       */
      readonly in3d?: Vec3;
    }
  /**
   * A segment, and where its ends are in space when they know.
   *
   * `in3d` is carried for the same reason a point carries it: the page has thrown the third
   * coordinate away, and several questions about a projected figure cannot be answered without
   * it. It is what lets `intersect` give back a point that still knows where it is — see the
   * note on the intersection evaluator — and so what lets the height of a pyramid come out
   * dashed. Absent for every plane figure, which pays nothing for it.
   */
  | {
      readonly kind: 'segment';
      readonly segment: Segment;
      readonly in3d?: readonly [Vec3, Vec3];
    }
  | { readonly kind: 'ray'; readonly ray: Ray }
  | { readonly kind: 'line'; readonly line: Line }
  | { readonly kind: 'vector'; readonly from: Vec2; readonly to: Vec2 }
  | { readonly kind: 'circle'; readonly circle: Circle }
  | { readonly kind: 'arc'; readonly arc: Arc }
  | { readonly kind: 'ellipse'; readonly ellipse: Ellipse }
  /**
   * A closed region.
   *
   * `in3d` is present when the region really lies in space — a plane's section through a solid,
   * or a polygon whose every corner came from one. It is what lets the drawing dash the edges
   * that run round the back, and what lets a figure be checked for being a *possible* one: four
   * corners that are not coplanar are not a section of anything, however convincing the shading.
   */
  | {
      readonly kind: 'polygon';
      readonly vertices: Polygon;
      readonly in3d?: readonly Vec3[];
      /**
       * The projection this region was drawn through, when it is a **free plane**.
       *
       * A section of a solid does not need it: everything built on a section asks the *solid* for
       * the frame, because the solid is right there as a parent. A blackboard plane has no solid,
       * and the things built on it — a point lying in it, the giao tuyến with another plane —
       * still have to put an answer worked out in space back on the page.
       *
       * Carried on the value rather than named again in each source, so a point on a plane cannot
       * end up projected through a different frame from the plane it is on. The plane knows; it
       * says so; and nothing downstream has to be told twice.
       */
      readonly frame?: SolidFrame;
    }
  | { readonly kind: 'polyline'; readonly points: readonly Vec2[]; readonly closed: boolean }
  /**
   * A plotted function.
   *
   * **Segments**, not a single point list, and that is the whole design. A function is drawn
   * as one run per continuous piece, so `1/x` is two curves rather than two curves joined by a
   * vertical line through the asymptote — which is the single most recognisable mark of a
   * plotting tool nobody finished.
   */
  /**
   * Coordinate axes, already reduced to what a painter needs.
   *
   * The geometry is computed in the evaluator rather than in the renderer, for the same reason
   * a solid's edges are: three back-ends draw this — canvas, SVG and PDF — and any arithmetic
   * left to them is arithmetic that can be got subtly differently three times.
   */
  | {
      readonly kind: 'axes';
      readonly origin: Vec2;
      readonly xAxis: Segment;
      readonly yAxis: Segment;
      /** Tick marks, as short segments crossing their axis. */
      readonly ticks: readonly Segment[];
      /** Numbers to write, already placed. Empty when the axes are unnumbered. */
      readonly marks: readonly { readonly at: Vec2; readonly text: string }[];
      /**
       * The letters naming the axes and the origin, already placed.
       *
       * Separate from `marks` because they are not read the same way. A tick number is a value on
       * a scale and belongs to the quiet register the rest of the numbers are in; `x`, `y` and `O`
       * name the parts of the figure, and a reader looks for them the way they look for a vertex
       * label. Keeping them apart is what lets the painters give them their own weight without
       * every tick number following along.
       */
      readonly names: readonly { readonly at: Vec2; readonly text: string }[];
    }
  | {
      readonly kind: 'function-graph';
      readonly segments: readonly (readonly Vec2[])[];
      /** Carried through so a label or a tooltip can say what was drawn. */
      readonly expression: string;
      /**
       * The range of `x` that was plotted.
       *
       * Here so that anything derived from the curve reads the same window the curve was drawn
       * in. A variation table falls back to it when the whole line has no finite table, and
       * taking it from the value rather than from the graph's source is what stops the two
       * describing different intervals.
       */
      readonly from: number;
      readonly to: number;
      /**
       * World units per mathematical unit, carried through for the same reason as the range.
       *
       * Anything built *on* the curve — a tangent at a given `x`, a shaded region read against
       * it — has to put its answer on the page in world units, and taking the number from the
       * value rather than from the graph's source is what stops a tangent being drawn at one
       * scale against a curve drawn at another.
       */
      readonly scale: number;
      /**
       * Where the formula is written, already placed.
       *
       * Computed here rather than in each painter for the reason the ticks are: three back-ends
       * each working out the same position is three chances to work it out differently, and the
       * one that matters is the hit test — a label a user can see but cannot grab because picking
       * looked somewhere else is worse than no label at all.
       *
       * `null` when there is no curve to hang it from.
       */
      readonly labelAt: Vec2 | null;
    }
  /**
   * The marked points of a graph: dots, and either a coordinate each or a guide to each axis.
   *
   * The same two drawing ingredients as `axes` and `variation-table`, plus the dots — so the
   * canvas, the SVG and the PDF have nothing to do but stroke segments, place points and centre
   * strings, and so nothing to disagree about.
   */
  | {
      readonly kind: 'graph-marks';
      readonly points: readonly Vec2[];
      /** Dashed guides to the axes. Empty unless the marks were asked for them. */
      readonly rules: readonly Segment[];
      /** Already placed: beside a dot, or under the axis where a guide lands. */
      readonly marks: readonly { readonly at: Vec2; readonly text: string }[];
    }
  /**
   * A variation table, reduced to lines and centred text.
   *
   * Deliberately the same two ingredients as `axes`, and for a reason beyond tidiness: the
   * PDF exporter writes Latin-1, so `∞`, `↗` and `‖` cannot survive as characters. They are
   * *drawn* — see `variationTable.ts` — which leaves the three back-ends with nothing to do
   * but stroke segments and centre strings, and so nothing to disagree about.
   */
  | {
      readonly kind: 'variation-table';
      readonly rules: readonly Segment[];
      readonly marks: readonly { readonly at: Vec2; readonly text: string }[];
      /** The top-left corner, which is what a drag moves. */
      readonly corner: Vec2;
      readonly width: number;
      readonly height: number;
    }
  | {
      readonly kind: 'text';
      readonly position: Vec2;
      readonly content: string;
      readonly latex: boolean;
    }
  /**
   * A drawn angle mark, resolved to everything a renderer needs.
   *
   * Directions rather than the original points, and `degrees` and `isRight` decided here
   * rather than at paint time — so the canvas, an SVG and a PDF cannot disagree about
   * whether an angle is right. `armLength` travels with it because the mark has to be
   * clamped to fit inside the angle's arms, and only the geometry knows how long they are.
   */
  | {
      readonly kind: 'angle-mark';
      readonly vertex: Vec2;
      /** Unit vector along the first arm. */
      readonly from: Vec2;
      /** Unit vector along the second arm. */
      readonly to: Vec2;
      readonly degrees: number;
      readonly isRight: boolean;
      readonly ticks: number;
      readonly showValue: boolean;
      /** Length of the shorter arm, in world units. */
      readonly armLength: number;
      /** The arc's size multiplier, carried so all three back-ends draw the same mark. */
      readonly size: number;
      /**
       * Where the user dragged the measure to, relative to its default place. Zero when they
       * have not, so the ordinary case needs no special handling anywhere downstream.
       */
      readonly valueOffset: Vec2;
    }
  | {
      readonly kind: 'measurement';
      readonly position: Vec2;
      readonly value: MeasurementValue;
    }
  /**
   * A viewpoint, and nothing else. The value of a `space`.
   *
   * ## Why a value with nothing to draw
   *
   * Because a figure of two planes meeting has no solid in it, and still has to put things worked
   * out in *space* onto the page. That is what a frame does, and a solid was the only thing that
   * had one. This is the frame on its own, so a plane can hang off it the way it hangs off a
   * solid — same field, same helper, no second path through the evaluator.
   *
   * ## Why a kind of its own rather than a `solid` with no edges
   *
   * That was the tempting shortcut: every reader already handles `solid`, so an empty one would
   * have cost nothing to add. It would also have been a **lie** — the object list would call a
   * viewpoint "Khối", `cuttable` would have to be taught which solids are not solids, and every
   * geometric question asked of a solid would quietly be asked of this. A kind that says what it
   * is costs one branch in each reader and never surprises one.
   *
   * Every painter, exporter and hit test therefore draws and finds **nothing** here, which is
   * correct: a viewpoint is not on the page. The entity is invisible as well, so the two agree.
   */
  | {
      readonly kind: 'space';
      readonly frame: SolidFrame;
    }
  /**
   * A solid, already projected to the drawing plane.
   *
   * The wireframe rather than the solid itself: hidden-line removal happens during
   * evaluation, so everything downstream — painting, hit testing, bounds — works in two
   * dimensions exactly as it does for every other value. That is what keeps solid geometry
   * from needing a second renderer.
   */
  | {
      readonly kind: 'solid';
      readonly origin: Vec2;
      readonly visible: readonly Segment[];
      readonly hidden: readonly Segment[];
      /**
       * The parts of the figure that are exactly conics: a sphere's outline and rings, and the
       * rims of a cylinder, a cone or a frustum.
       *
       * Nothing a reader sees is a chord, so the figure stays round at any magnification. The
       * segments above remain because bounds, hit-testing and the spatial index all measure
       * rather than draw, and none of them should have to learn about conics to frame a picture.
       */
      readonly curves?: readonly SolidCurve[];
      /**
       * What a painter draws *beside* the curves — present whenever `curves` is.
       *
       * A cylinder is a real mixture: its rims are circles and its sides are straight, and both
       * have to reach the page. The rim chords are missing here precisely because the curve
       * already covers them, and drawing both would lay a polyline over its own ellipse.
       */
      readonly straight?: {
        readonly visible: readonly Segment[];
        readonly hidden: readonly Segment[];
      };
      /**
       * Face rings and the projection, present with `points` and for the same reason.
       *
       * They are what a **plane** needs: the faces to clip a line against, and the frame to put
       * something worked out in space back on the page. Four points settle a parallel projection
       * completely, so `frame` is four points rather than a function — a value stays data.
       */
      readonly faces?: readonly (readonly number[])[];
      readonly frame?: SolidFrame;
      /**
       * The radii and taper of a *round* solid, with its projection.
       *
       * What a section of a cylinder, a cone, a frustum or a ball is worked out from. Carried
       * instead of the faces, because those bodies have no flat sides to clip against and a
       * section of one is a circle rather than a polygon.
       */
      readonly round?: { readonly body: RoundBody; readonly frame: SolidFrame };
      /**
       * The named corners, in the order the figure numbers them, on the page and in space.
       *
       * Present on a `solid-figure` and absent on the shape tool's solid, and the difference is
       * the whole point of the two existing: one is a picture of a solid and the other is a
       * solid a problem can name the corners of.
       */
      readonly points?: readonly Vec2[];
      readonly in3d?: readonly Vec3[];
      /**
       * The named edges, as pairs of indices into {@link points}.
       *
       * What lets a click on a drawn edge say *which* edge it was. `visible` and `hidden` cannot:
       * their split is a fact about the current rotation, so an index into either stops meaning
       * the same edge the moment the solid turns.
       *
       * Absent on the shape tool's block, which has no named corners at all — and therefore
       * nothing on its edges that could be said to be anywhere in space.
       */
      readonly edges?: readonly (readonly [number, number])[];
      /** Extents in space, so a construction can ask for a rim point or a perpendicular foot. */
      readonly extent?: { readonly width: number; readonly height: number; readonly depth: number };
      /**
       * The planes cutting a **sphere**, as signed distances from its centre.
       *
       * Carried beside {@link extent} rather than folded into it, because a section is not a
       * measurement of the body — it is a choice the problem made about it, and a ring on it that
       * a point can sit on. `rim` reads this to know both where the ring is and how wide it is:
       * `r = √(R² − d²)`, so the distance is the only number either needs.
       */
      readonly sections?: readonly { readonly at: number; readonly tilt?: number }[];
    }
  | {
      readonly kind: 'image';
      readonly position: Vec2;
      readonly width: number;
      readonly height: number;
      readonly href: string;
    };

/**
 * The result of a measurement.
 *
 * `scalar` covers length, angle, area and slope; `point` covers a coordinate readout;
 * `vector` covers component display. `unit` is carried so the formatter does not have to
 * re-derive it from the measure kind at render time.
 */
export type MeasurementValue =
  | { readonly type: 'scalar'; readonly value: number; readonly unit: string }
  | { readonly type: 'point'; readonly value: Vec2 }
  | { readonly type: 'vector'; readonly value: Vec2 };

/* ------------------------------------------------------------------------- curves */

/**
 * Anything a point can lie on, be projected onto, or intersect with.
 *
 * Several constructions — locus points, projection, intersection, tangents — care only
 * that their parent *is a curve*, not which kind. Funnelling them through one type keeps
 * the evaluator from repeating a twelve-case dispatch per operation.
 */
export type Curve =
  | { readonly kind: 'line'; readonly line: Line }
  | { readonly kind: 'ray'; readonly ray: Ray }
  | { readonly kind: 'segment'; readonly segment: Segment }
  | { readonly kind: 'circle'; readonly circle: Circle }
  | { readonly kind: 'arc'; readonly arc: Arc }
  | { readonly kind: 'ellipse'; readonly ellipse: Ellipse };

/* ---------------------------------------------------------------------- accessors */

export function asPoint(value: Evaluated | null | undefined): Vec2 | null {
  return value?.kind === 'point' ? value.point : null;
}

/**
 * The curve a value represents, if any.
 *
 * A `vector` is treated as the segment from its tail to its head: a student who
 * constructs the midpoint of a vector means the midpoint of that segment.
 */
export function asCurve(value: Evaluated | null | undefined): Curve | null {
  if (value == null) return null;
  switch (value.kind) {
    // A viewpoint: no geometry on the page at all.
    case 'space':
      return null;

    /*
     * Not a curve the kernel can construct on. A plotted function is a heap of sampled points
     * with no closed form, so there is no exact intersection, projection or tangent to be had —
     * and offering an approximate one would quietly break the promise that every construction
     * in this editor is exact.
     */
    case 'function-graph':
    case 'axes':
    case 'variation-table':
    case 'graph-marks':
      return null;

    case 'line':
      return { kind: 'line', line: value.line };
    case 'ray':
      return { kind: 'ray', ray: value.ray };
    case 'segment':
      return { kind: 'segment', segment: value.segment };
    case 'vector':
      return { kind: 'segment', segment: { a: value.from, b: value.to } };
    case 'circle':
      return { kind: 'circle', circle: value.circle };
    case 'arc':
      return { kind: 'arc', arc: value.arc };
    case 'ellipse':
      return { kind: 'ellipse', ellipse: value.ellipse };
    case 'point':
    case 'polygon':
    case 'polyline':
    case 'text':
    case 'measurement':
    case 'image':
    case 'solid':
    case 'angle-mark':
      return null;
  }
}

/**
 * The curve a locus point is actually attached to: a whole value, or one side of a polygon.
 *
 * The single place that answers "what am I on?", because four places used to ask it — the
 * evaluator, the drag tool, the snapper and the re-parameteriser — and every one of them called
 * {@link asCurve} directly. Adding polygon sides to only three of them is the way a point ends
 * up drawn on a triangle's edge and dragged along a circle.
 *
 * A polygon is deliberately still not a curve to {@link asCurve}. It is a region with a boundary
 * made of several curves, and the honest answer to "give me the curve" is that there isn't one.
 * What there is, is a *named side* — which this returns, and which is an ordinary segment with an
 * ordinary parameter along it.
 *
 * `null` when the side does not exist: a pentagon cut down to a triangle has no fourth edge, and
 * a point that claimed one evaluates to nothing rather than to somewhere plausible. The index
 * survives in the source, so restoring the sides restores the point.
 */
export function hostCurve(value: Evaluated | null | undefined, edge?: number): Curve | null {
  if (edge === undefined) return asCurve(value);
  if (value?.kind !== 'polygon') return null;

  const vertices = value.vertices;
  if (!Number.isInteger(edge) || edge < 0 || edge >= vertices.length) return null;

  const a = vertices[edge];
  const b = vertices[(edge + 1) % vertices.length];
  if (a === undefined || b === undefined) return null;
  return { kind: 'segment', segment: { a, b } };
}

/**
 * How many sides a value offers to {@link hostCurve}. Zero for anything that is not a polygon.
 *
 * Used by the snapper to decide whether an entity is worth measuring edge by edge.
 *
 * A `polyline` is excluded on purpose, though its segments are just as nameable. The polylines
 * in this product are mostly freehand strokes, and a freehand stroke has hundreds of segments
 * that exist because of how fast the hand moved. Attaching a point to segment 137 of a scribble
 * is a relationship the drawing never claimed, and offering it would put a snap target every few
 * pixels along every stroke on the page.
 */
export function edgeCount(value: Evaluated | null | undefined): number {
  return value?.kind === 'polygon' ? value.vertices.length : 0;
}

/**
 * A solid's named edges, each as the two corners it joins and the segment it is drawn as.
 *
 * Empty for anything that is not a solid with named corners — the shape tool's block, a sphere,
 * the curved part of a cone. That emptiness is the honest answer rather than a shortfall: a
 * point on a cone's rim is not on a line between two corners, and the product has
 * `solid-rim-point` for saying where it actually is.
 *
 * ## Why a solid's edge is not treated like a polygon's
 *
 * A polygon is flat, so a point on its side is fully described by a fraction along that side.
 * A solid is not: its edges are lines **in space** seen in projection, and a point on `SA` has a
 * position in space that decides whether later constructions through it are hidden, whether an
 * angle at it is truly right, and where it goes when the solid turns. Attaching a flat locus
 * point to the drawn line would throw all of that away and produce a mark that merely looks
 * right from the current angle.
 *
 * So this returns the *corners*, and the point built from them divides them in space.
 */
export function solidEdges(
  value: Evaluated | null | undefined
): readonly { readonly a: number; readonly b: number; readonly segment: Segment }[] {
  if (value?.kind !== 'solid') return [];
  const { points, edges } = value;
  if (points === undefined || edges === undefined) return [];

  return edges.flatMap(([a, b]) => {
    const from = points[a];
    const to = points[b];
    return from === undefined || to === undefined ? [] : [{ a, b, segment: { a: from, b: to } }];
  });
}

/**
 * The infinite line through a linear value.
 *
 * Used by `parallel-line`, `perpendicular-line` and reflection, all of which accept any
 * linear parent and care only about its direction and position.
 */
export function asLine(value: Evaluated | null | undefined): Line | null {
  if (value == null) return null;
  switch (value.kind) {
    // A viewpoint: no geometry on the page at all.
    case 'space':
      return null;

    // A plotted curve is not straight, and a pair of axes is two lines rather than one.
    case 'function-graph':
    case 'axes':
    case 'variation-table':
    case 'graph-marks':
      return null;

    case 'line':
      return value.line;
    case 'ray':
      return { origin: value.ray.origin, direction: value.ray.direction };
    case 'segment':
      return segmentToLine(value.segment);
    case 'vector':
      return segmentToLine({ a: value.from, b: value.to });
    case 'point':
    case 'circle':
    case 'arc':
    case 'ellipse':
    case 'polygon':
    case 'polyline':
    case 'text':
    case 'measurement':
    case 'image':
    case 'solid':
    case 'angle-mark':
      return null;
  }
}

export function asCircle(value: Evaluated | null | undefined): Circle | null {
  if (value == null) return null;
  if (value.kind === 'circle') return value.circle;
  if (value.kind === 'arc') return { center: value.arc.center, radius: value.arc.radius };
  return null;
}

/** The vertices of a polygon or closed polyline. */
export function asVertices(value: Evaluated | null | undefined): Polygon | null {
  if (value == null) return null;
  if (value.kind === 'polygon') return value.vertices;
  if (value.kind === 'polyline') return value.points;
  return null;
}

/* -------------------------------------------------------------------- constructors */

export function evaluatedPoint(point: Vec2): Evaluated {
  return { kind: 'point', point };
}

/**
 * Guards a computed point before it enters the graph.
 *
 * A single `NaN` reaching the scene propagates silently through every dependent node
 * and surfaces much later as geometry that has vanished for no visible reason. Rejecting
 * it here turns that into a clean "this construction is undefined".
 */
export function finitePoint(point: Vec2): Evaluated | null {
  return vec.isFinite(point) ? { kind: 'point', point } : null;
}

/**
 * The same guard, for a point that came from a solid and remembers where.
 *
 * Both halves are checked. A finite page position with a `NaN` behind it is the worse of the two
 * failures: it draws, so nothing looks wrong, and every question asked of it afterwards — is
 * this line covered, is that angle right — quietly answers no.
 */
export function finiteSpacePoint(point: Vec2, in3d: Vec3): Evaluated | null {
  if (!vec.isFinite(point)) return null;
  if (!Number.isFinite(in3d.x) || !Number.isFinite(in3d.y) || !Number.isFinite(in3d.z)) return null;
  return { kind: 'point', point, in3d };
}
